import React, { useState, useEffect, useCallback } from 'react';
import { Gift, Ticket, CheckCircle2, AlertCircle } from 'lucide-react';
import { useUIStore } from '../../stores/useUIStore';
import { useAuthStore } from '../../stores/useAuthStore';

interface PastWinner {
  id: number;
  username: string;
  prize: string;
  date: string;
}

// Fallback inicial con el historial real en caso de que la API tarde en responder
const FALLBACK_WINNERS: PastWinner[] = [
  {
    id: 3,
    username: 'eddalexpro',
    prize: '800 Pavos en Saldo',
    date: '02/11/2026',
  },
  {
    id: 2,
    username: 'Shadow_of_dog',
    prize: '800 Pavos en Saldo',
    date: '26/10/2026',
  },
  {
    id: 1,
    username: 'T44DEO1',
    prize: '800 Pavos en Saldo',
    date: '19/10/2026',
  },
];

export const SorteosPage: React.FC = () => {
  const openAuth = useUIStore((s) => s.openAuthModal);
  const user = useAuthStore((s) => s.user);

  const [isEnrolled, setIsEnrolled] = useState<boolean>(() => {
    try {
      return (
        localStorage.getItem('gf_raffle_enrolled') === 'true' ||
        localStorage.getItem('gf_raffle_enrolled_3') === 'true'
      );
    } catch {
      return false;
    }
  });

  const [loadingEnroll, setLoadingEnroll] = useState(false);
  const [enrollError, setEnrollError] = useState<string | null>(null);
  const [pastWinners, setPastWinners] = useState<PastWinner[]>(FALLBACK_WINNERS);
  const [activeRaffle, setActiveRaffle] = useState<any>(null);

  // Countdown timer
  const [countdown, setCountdown] = useState({ days: '00', hours: '00', mins: '00', secs: '00' });
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  // 1. Cargar historial público de ganadores desde la API
  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch('/api/raffles/history');
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          const formatted: PastWinner[] = data.map((raf: any) => {
            const winner = raf.winners?.[0];
            const rawDate = winner?.won_at || raf.end_date;
            let dateStr = 'Reciente';
            if (rawDate) {
              const d = new Date(rawDate);
              if (!isNaN(d.getTime())) {
                dateStr = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
              }
            }
            return {
              id: raf.id,
              username: winner?.username || 'Ganador',
              prize: raf.prize_title || `${raf.prize_amount || 800} Pavos en Saldo`,
              date: dateStr,
            };
          });
          setPastWinners(formatted);
        }
      }
    } catch {
      // Usar fallback inicial silenciosamente
    }
  }, []);

  // 2. Cargar sorteo activo y chances del usuario desde la API
  const fetchActiveRaffle = useCallback(async () => {
    try {
      const token =
        localStorage.getItem('fn_token') ||
        localStorage.getItem('token') ||
        localStorage.getItem('auth_token');

      const res = await fetch('/api/raffles/active', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setActiveRaffle(data);
        if (typeof data.seconds_left === 'number') {
          setSecondsLeft(data.seconds_left);
        }
        if (data?.user_status) {
          setIsEnrolled(Boolean(data.user_status.is_registered));
        }
      }
    } catch {
      // Ignorar error de red y usar valores calculados
    }
  }, []);

  useEffect(() => {
    fetchHistory();
    fetchActiveRaffle();
  }, [fetchHistory, fetchActiveRaffle, user]);

  // Manejo del contador regresivo con sincronización del servidor
  useEffect(() => {
    const updateCountdown = () => {
      let diffSecs = 0;
      if (typeof secondsLeft === 'number' && secondsLeft >= 0) {
        diffSecs = secondsLeft;
      } else {
        // Cálculo fallback si aún no cargó el endpoint (domingo 21:00 ART == lunes 00:00 UTC)
        const now = new Date();
        const target = new Date(now);
        const day = now.getUTCDay();
        const diffToSunday = (7 - day) % 7;
        target.setUTCDate(now.getUTCDate() + (diffToSunday === 0 && now.getUTCHours() >= 23 ? 7 : diffToSunday));
        target.setUTCHours(23, 59, 59, 0);
        diffSecs = Math.max(0, Math.floor((target.getTime() - now.getTime()) / 1000));
      }

      const d = String(Math.floor(diffSecs / (3600 * 24))).padStart(2, '0');
      const h = String(Math.floor((diffSecs % (3600 * 24)) / 3600)).padStart(2, '0');
      const m = String(Math.floor((diffSecs % 3600) / 60)).padStart(2, '0');
      const s = String(diffSecs % 60).padStart(2, '0');

      setCountdown({ days: d, hours: h, mins: m, secs: s });
    };

    updateCountdown();

    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (typeof prev === 'number') {
          return Math.max(0, prev - 1);
        }
        return prev;
      });
      updateCountdown();
    }, 1000);

    return () => clearInterval(interval);
  }, [secondsLeft]);

  const handleEnroll = async () => {
    if (!user) {
      openAuth('login');
      return;
    }

    setLoadingEnroll(true);
    setEnrollError(null);
    try {
      const token =
        localStorage.getItem('fn_token') ||
        localStorage.getItem('token') ||
        localStorage.getItem('auth_token');

      const res = await fetch('/api/raffles/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setEnrollError(data.detail || 'No se pudo completar la inscripción.');
        return;
      }

      localStorage.setItem('gf_raffle_enrolled', 'true');
      setIsEnrolled(true);
      await fetchActiveRaffle();
    } catch {
      setEnrollError('Error de conexión al inscribirte en el sorteo.');
    } finally {
      setLoadingEnroll(false);
    }
  };

  const prizeTitle = activeRaffle?.prize_title || '800 Pavos en Saldo';
  const totalTickets = activeRaffle?.user_status?.total_tickets ?? (isEnrolled ? 1 : 0);
  const ticketHeadline =
    totalTickets === 0
      ? '0 Chances Activas'
      : totalTickets === 1
      ? '1 Chance Activa'
      : `${totalTickets} Chances Activas`;

  const progressVbucks = activeRaffle?.user_status?.next_ticket_progress_vbucks ?? 0;
  const progressPct = Math.min(100, Math.max(0, Math.round((progressVbucks / 1000) * 100)));

  return (
    <div className="w-full min-h-screen bg-transparent text-[#06173f] py-8 sm:py-12 px-4 sm:px-6">
      <div className="max-w-[1200px] mx-auto space-y-8 min-w-0">
        {/* 1. CABECERA 1:1 CON sorteos.html */}
        <header className="space-y-1">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-[#06173f] font-heading">
            Sorteos Semanales
          </h1>
          <p className="text-sm sm:text-base text-[var(--soft)] font-medium max-w-2xl leading-relaxed">
            ¡Participa gratis cada semana sin necesidad de compras! Sorteamos 800 Pavos en Saldo todos los domingos. Cada 1.000 Pavos que compres en la semana suman 1 chance extra.
          </p>
        </header>

        {/* 2. GRID DE 2 TARJETAS SIMÉTRICAS (.raf-grid) */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
          style={{ filter: 'drop-shadow(6px 6px 0 rgba(20, 71, 230, 0.14))' }}
        >
          {/* TARJETA 1: SORTEO SEMANAL */}
          <section
            className="p-5 sm:p-7 bg-white border-2 border-[var(--line)] hover:border-[#1447e6] transition flex flex-col justify-between space-y-4 sm:space-y-5 min-w-0"
            style={{ clipPath: 'var(--cut)' }}
            aria-label="Sorteo Semanal Activo"
          >
            <div>
              <div className="flex items-center justify-between mb-3.5">
                <span
                  className="inline-flex items-center gap-2 bg-[var(--ice)] text-[#1447e6] font-heading font-black text-xs uppercase px-3 py-1"
                  style={{ clipPath: 'var(--slash)' }}
                >
                  <span className="w-2 h-2 rounded-full bg-[#1447e6] animate-pulse" />
                  <span>Activo</span>
                </span>
              </div>

              {/* Vitrina con Hexágono y Premio */}
              <div
                className="bg-[var(--ice)] p-5 mb-4 flex flex-col items-center justify-center text-center min-h-[160px]"
                style={{ clipPath: 'var(--cut)' }}
              >
                <div
                  className="w-14 h-14 bg-[#1447e6] text-white flex items-center justify-center text-2xl shadow-md mb-2"
                  style={{ clipPath: 'var(--cut)' }}
                >
                  <Gift className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-xl sm:text-2xl font-black uppercase text-[#06173f] font-heading">
                  {prizeTitle}
                </h2>
                <span className="text-xs text-[var(--soft)] font-medium mt-0.5">
                  Acreditación directa en tu billetera GF
                </span>
              </div>

              {/* 4 Cajas de Cuenta Regresiva */}
              <div className="grid grid-cols-4 gap-1.5 sm:gap-2 mb-4" aria-label="Cuenta regresiva">
                <div
                  className="bg-[var(--ice)] border border-[var(--line)] py-1.5 sm:py-2 text-center min-w-0"
                  style={{ clipPath: 'var(--slash-sm)' }}
                >
                  <div className="font-heading font-black text-base sm:text-xl text-[#06173f] tabular-nums">
                    {countdown.days}
                  </div>
                  <div className="font-heading font-bold text-[9px] sm:text-[10px] uppercase text-[var(--soft)] mt-0.5 tracking-wider">
                    Días
                  </div>
                </div>
                <div
                  className="bg-[var(--ice)] border border-[var(--line)] py-1.5 sm:py-2 text-center min-w-0"
                  style={{ clipPath: 'var(--slash-sm)' }}
                >
                  <div className="font-heading font-black text-base sm:text-xl text-[#06173f] tabular-nums">
                    {countdown.hours}
                  </div>
                  <div className="font-heading font-bold text-[9px] sm:text-[10px] uppercase text-[var(--soft)] mt-0.5 tracking-wider">
                    Horas
                  </div>
                </div>
                <div
                  className="bg-[var(--ice)] border border-[var(--line)] py-1.5 sm:py-2 text-center min-w-0"
                  style={{ clipPath: 'var(--slash-sm)' }}
                >
                  <div className="font-heading font-black text-base sm:text-xl text-[#06173f] tabular-nums">
                    {countdown.mins}
                  </div>
                  <div className="font-heading font-bold text-[9px] sm:text-[10px] uppercase text-[var(--soft)] mt-0.5 tracking-wider">
                    Min
                  </div>
                </div>
                <div
                  className="bg-[var(--ice)] border border-[var(--line)] py-1.5 sm:py-2 text-center min-w-0"
                  style={{ clipPath: 'var(--slash-sm)' }}
                >
                  <div className="font-heading font-black text-base sm:text-xl text-[#06173f] tabular-nums">
                    {countdown.secs}
                  </div>
                  <div className="font-heading font-bold text-[9px] sm:text-[10px] uppercase text-[var(--soft)] mt-0.5 tracking-wider">
                    Seg
                  </div>
                </div>
              </div>
            </div>

            {/* Pie de Tarjeta 1 */}
            <div className="pt-3.5 border-t-2 border-[var(--line)] space-y-2">
              {enrollError && (
                <div className="p-2.5 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded flex items-center gap-1.5">
                  <AlertCircle className="w-4 h-4 shrink-0 text-rose-500" />
                  <span>{enrollError}</span>
                </div>
              )}

              {isEnrolled ? (
                <div
                  className="w-full h-11 flex items-center justify-center gap-2 bg-[var(--ice)] border-2 border-[var(--line)] text-[#06173f] font-heading font-black text-xs uppercase"
                  style={{ clipPath: 'var(--slash)' }}
                >
                  <CheckCircle2 className="w-4 h-4 text-[#1447e6]" />
                  <span>Inscripción activa</span>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleEnroll}
                  disabled={loadingEnroll}
                  className="btn w-full h-11 bg-[#1447e6] hover:bg-[#0a2a9c] text-white font-heading font-black uppercase tracking-wider text-xs transition flex items-center justify-center gap-2 shadow-md cursor-pointer disabled:opacity-50"
                  style={{ clipPath: 'var(--slash)', color: '#ffffff' }}
                >
                  <Gift className="w-4 h-4 text-white" />
                  <span>{loadingEnroll ? 'Inscribiendo...' : 'Inscribirme Gratis'}</span>
                </button>
              )}
            </div>
          </section>

          {/* TARJETA 2: TUS CHANCES Y PROGRESO */}
          <section
            className="p-5 sm:p-7 bg-white border-2 border-[var(--line)] hover:border-[#1447e6] transition flex flex-col justify-between space-y-4 sm:space-y-5 min-w-0"
            style={{ clipPath: 'var(--cut)' }}
            aria-label="Tus chances acumuladas"
          >
            <div>
              <div className="flex items-center justify-between mb-3.5">
                <span
                  className="inline-flex items-center gap-2 bg-[var(--ice)] text-[#1447e6] font-heading font-black text-xs uppercase px-3 py-1"
                  style={{ clipPath: 'var(--slash)' }}
                >
                  <Ticket className="w-3.5 h-3.5 text-[#1447e6]" />
                  <span>Tus Chances</span>
                </span>
              </div>

              {/* Vitrina Chances */}
              <div
                className="bg-[var(--ice)] p-5 mb-4 flex flex-col items-center justify-center text-center min-h-[160px]"
                style={{ clipPath: 'var(--cut)' }}
              >
                <div
                  className="w-14 h-14 bg-[#1447e6] text-white flex items-center justify-center text-2xl shadow-md mb-2"
                  style={{ clipPath: 'var(--cut)' }}
                >
                  <Ticket className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-xl sm:text-2xl font-black uppercase text-[#06173f] font-heading">
                  {ticketHeadline}
                </h2>
                <span className="text-xs text-[var(--soft)] font-medium mt-0.5">
                  {isEnrolled ? '¡Ya estás participando este domingo!' : 'Inscríbete gratis para participar'}
                </span>
              </div>

              {/* Barra de Progreso Ticket Extra */}
              <div
                className="bg-[var(--ice)] border border-[var(--line)] p-3.5"
                style={{ clipPath: 'var(--slash)' }}
              >
                <div className="flex justify-between items-center text-xs mb-2">
                  <span className="font-semibold text-[var(--soft)]">Próximo ticket extra</span>
                  <b className="font-heading font-black text-[#1447e6]">{progressVbucks} / 1.000 Pavos</b>
                </div>
                <div className="relative h-2.5 bg-slate-300 overflow-hidden" style={{ clipPath: 'polygon(4px 0, 100% 0, calc(100% - 4px) 100%, 0 100%)' }}>
                  <div
                    className="h-full bg-[#1447e6] transition-all duration-500"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* 3. ÚLTIMOS GANADORES (CARGADOS DINÁMICAMENTE DESDE LA BASE DE DATOS) */}
        <section aria-label="Últimos ganadores" className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xl sm:text-2xl font-black uppercase text-[#06173f] font-heading tracking-tight">
              Últimos Ganadores
            </h2>
          </div>

          <div className="space-y-2">
            {pastWinners.map((win) => (
              <div
                key={win.id}
                className="bg-white border-2 border-[var(--line)] hover:border-[#1447e6] p-3 sm:p-4 flex items-center justify-between gap-2.5 transition min-w-0"
                style={{ clipPath: 'var(--slash)' }}
              >
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                  <div
                    className="w-8 h-8 sm:w-9 sm:h-9 bg-[var(--ice)] text-[#1447e6] flex items-center justify-center font-black text-xs sm:text-sm shrink-0"
                    style={{ clipPath: 'var(--slash-sm)' }}
                  >
                    #{win.id}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-black text-xs sm:text-base text-[#06173f] uppercase truncate" title={win.username}>
                      {win.username}
                    </div>
                    <div className="text-[11px] sm:text-xs text-[var(--soft)] font-medium truncate">
                      Sorteado el {win.date}
                    </div>
                  </div>
                </div>

                <div className="font-heading font-black text-xs sm:text-sm md:text-base text-[#1447e6] shrink-0 text-right whitespace-nowrap pl-1">
                  {win.prize}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default SorteosPage;
